<template>
  <div v-if="entryReady" class="work-competition">
    <section :class="['hero', { 'hero--cashmere': isCashmereActivity }]">
      <img
        v-if="isCashmereActivity"
        :src="cashmereReviewTop"
        alt="Young·绒—2026第二届中国创新创意设计大赛"
        class="hero-cashmere-image"
      >
      <template v-else>
        <div class="hero-gradient" />
        <div class="hero-glow hero-glow--left" />
        <div class="hero-glow hero-glow--right" />
        <div class="hero-ribbon hero-ribbon--top" />
        <div class="hero-ribbon hero-ribbon--bottom" />
        <div class="hero-copy">
          <p class="hero-kicker">欢迎参与</p>
          <h1>{{ activityObj.title || defaultTitle }}</h1>
        </div>
      </template>
    </section>

    <section class="competition-content">
      <div class="summary">
        <p class="summary-title">{{ summaryTitle }}</p>
        <div v-if="isLogin && voteOpen === 1" class="summary-meta">
          <span>{{ voteModeLabel }}</span>
          <span>{{ voteSubmitted ? '已提交' : '已选' }} {{ selectedCount }} / {{ voteQuota }}</span>
          <span v-if="voteSubmitted">已锁定，前台不可修改</span>
        </div>
      </div>

      <aside v-if="showGraduationVotePanel" class="category-vote-panel" aria-label="类别投票统计">
        <div class="category-vote-panel__title">类别票数</div>
        <div class="category-vote-panel__body">
          <div
            v-for="item in graduationCategoryVoteStats"
            :key="item.id"
            :class="['category-vote-row', { 'is-short': item.selected < item.minimum }]"
          >
            <div class="category-vote-row__name">{{ item.name }}</div>
            <div class="category-vote-row__stats">
              <span>已投 {{ item.selected }} 票</span>
              <span>最低 {{ item.minimumText }}{{ item.minimumText === '待计算' ? '' : ' 票' }}</span>
            </div>
          </div>
        </div>
        <div class="category-vote-panel__footer">
          合计 {{ selectedCount }} / {{ voteQuota }}，总最低 {{ minimumTotalVotes }}
        </div>
      </aside>

      <div ref="worksListTop" class="tabs-box">
        <button
          type="button"
          :class="['tab-item', { active: activeCategoryId === 0 }]"
          @click="handleClickActive(0)"
        >
          全部
        </button>
        <button
          v-for="tab in categoryTabs"
          :key="tab.id"
          type="button"
          :class="['tab-item', { active: tab.id === activeCategoryId }]"
          @click="handleClickActive(tab.id)"
        >
          {{ tab.name }}
        </button>
      </div>

      <div class="search-box">
        <a-input-search
          v-model.trim="worksParams.keyword"
          class="search-input"
          placeholder="请输入作品名称关键词"
          enter-button="搜索"
          allow-clear
          @search="handleSearch"
        />
        <button
          v-if="worksParams.keyword"
          type="button"
          class="search-reset"
          @click="handleResetSearch"
        >
          清空
        </button>
      </div>

      <div v-if="loading" class="state-block">加载中...</div>
      <div v-else-if="worksList.length === 0" class="state-block">{{ emptyStateText }}</div>

      <div v-else class="grid">
        <article
          v-for="(work, index) in worksList"
          :key="work.id"
          class="card"
          @click="handleClickToDetails(work.id)"
        >
          <div class="cover-wrap">
            <img
              :src="coverImage(work)"
              :alt="work.name"
              class="cover"
              loading="lazy"
              decoding="async"
              :fetchpriority="index < 4 ? 'high' : 'low'"
              @error="handleImageError($event, work)"
            >
            <span class="rank-tag">No.{{ resolveRanking(work, index) }}</span>
          </div>

          <div class="card-body">
            <h3 class="title">{{ work.name }}</h3>
            <p class="meta-line">类别：{{ workTypeLabel(work) }}</p>
            <p class="meta-line">材质：{{ work.material || '-' }}</p>

            <div :class="['footer-row', { 'footer-row--compact': !shouldShowVoteCount() }]">
              <div v-if="shouldShowVoteCount()" class="votes">
                <strong>{{ displayVoteCount(work) }}</strong>
                <span>票</span>
              </div>
              <button
                type="button"
                :class="['vote-btn', voteButtonClass(work)]"
                :disabled="isVoteDisabled(work)"
                @click.stop="handleVote(work)"
              >
                {{ voteButtonText(work) }}
              </button>
            </div>
          </div>
        </article>
      </div>

      <div v-if="worksParams.total > worksParams.limit" class="tabs-page">
        <a-pagination
          size="small"
          :current="worksParams.page"
          :page-size="worksParams.limit"
          :total="worksParams.total"
          @change="handlePageChange"
        />
      </div>

      <div v-if="voteOpen === 1" class="submit-dock">
        <transition name="selected-panel">
          <div v-if="selectedPanelExpanded" class="selected-panel">
            <div class="selected-panel__header">
              <div>
                <strong>已选择的作品</strong>
                <span>共 {{ selectedCount }} 件</span>
              </div>
              <button
                type="button"
                class="selected-panel__close"
                aria-label="收起已选作品"
                title="收起"
                @click="selectedPanelExpanded = false"
              >
                <a-icon type="down" />
              </button>
            </div>

            <div v-if="selectedWorks.length" class="selected-list">
              <div v-for="work in selectedWorks" :key="work.id" class="selected-item">
                <img
                  :src="coverImage(work)"
                  :alt="work.name || '已选作品'"
                  class="selected-item__cover"
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                  @error="handleImageError($event, work)"
                >
                <div class="selected-item__content">
                  <strong>{{ work.name || '未命名作品' }}</strong>
                  <span>{{ selectedWorkMeta(work) }}</span>
                </div>
                <button
                  v-if="!voteSubmitted"
                  type="button"
                  class="selected-item__remove"
                  :disabled="isVoteMutating(work.id)"
                  title="取消选择"
                  @click="handleCancelSelected(work)"
                >
                  <a-icon type="close" />
                </button>
              </div>
            </div>
            <div v-else class="selected-panel__empty">暂未选择作品</div>
          </div>
        </transition>

        <div class="submit-bar">
          <button
            type="button"
            class="submit-bar__toggle"
            :class="{ expanded: selectedPanelExpanded }"
            :aria-expanded="String(selectedPanelExpanded)"
            title="查看已选作品"
            @click="selectedPanelExpanded = !selectedPanelExpanded"
          >
            <a-icon type="up" />
            <span>{{ selectedPanelExpanded ? '收起列表' : `查看已选（${selectedCount}）` }}</span>
          </button>
          <div class="submit-bar__info">
            <div class="submit-bar__title">{{ submitBarTitle }}</div>
            <div class="submit-bar__desc">{{ submitBarDesc }}</div>
          </div>
          <button
            type="button"
            class="submit-bar__btn"
            :class="{ disabled: submitButtonDisabled }"
            :disabled="submitButtonDisabled"
            @click="handleSubmitVotes"
          >
            {{ submitButtonText }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  activityDetail,
  getExpertVoteAccess,
  getWorksList,
  getCache,
  setCache,
  submitCheck,
  submitCache,
} from "@/api/Vote";
import { mapGetters } from "vuex";
import toLogin from "@/utils/login";
import nopic from "@/assets/images/nopic.png";
import {
  replaceBrokenImageFromCandidates,
  resolveWorkThumbnail,
  resolveWorkThumbnailCandidates,
} from "@/utils/workImage";
import { normalizeVoteMeta, normalizeVoteOptionGroups } from "@/utils/voteMeta";
import { cashmereAssets } from "@/constants/cashmereAssets";

const DEFAULT_ACTIVITY_ID = 5;
const CASHMERE_ACTIVITY_ID = 6;
const GRADUATION_EXPERT_MINIMUM_RATIO = 0.8;
const DEFAULT_TITLE = "2026第五届全国大学生毕业季工艺美术类优秀毕业作品大赛";
const LIST_SCROLL_STORAGE_KEY = "workCompetition:listScroll";

function createDefaultVoteState() {
  return {
    open: 0,
    submitted: 0,
    expertUser: 0,
    quota: 0,
    used: 0,
    residue: 0,
    message: "投票活动未开启",
  };
}

function createDefaultVoteCache(activityId = DEFAULT_ACTIVITY_ID) {
  return {
    activityId,
    categoryVos: [],
    plateVos: [],
    countWorks: 0,
    countCheckWorks: 0,
    expertVotes: 0,
    isVote: 0,
  };
}

export default {
  name: "WorkCompetition",
  data() {
    return {
      defaultTitle: DEFAULT_TITLE,
      cashmereReviewTop: cashmereAssets.reviewTop,
      loading: false,
      submittingCache: false,
      selectedPanelExpanded: false,
      mutatingWorkIds: [],
      activeCategoryId: 0,
      activityId: DEFAULT_ACTIVITY_ID,
      activityObj: {},
      categoryTabs: [],
      worksList: [],
      activityInitialized: false,
      entryReady: false,
      entryBlocked: false,
      activityRequestId: 0,
      worksRequestId: 0,
      categoryCountRequestId: 0,
      pendingRestoreScrollY: null,
      categoryWorkCounts: {},
      voteState: createDefaultVoteState(),
      voteCache: createDefaultVoteCache(),
      worksParams: {
        activityId: String(DEFAULT_ACTIVITY_ID),
        uid: 0,
        limit: 12,
        page: 1,
        total: 0,
        totalPage: 0,
        plateId: 0,
        categoryId: 0,
        keyword: "",
      },
    };
  },
  computed: {
    ...mapGetters(["userInfo", "isLogin"]),
    voteQuota() {
      return Number(this.voteState.quota || 0);
    },
    isCashmereActivity() {
      return Number(this.activityId) === CASHMERE_ACTIVITY_ID;
    },
    useCategoryFilter() {
      return Number(this.activityId) === DEFAULT_ACTIVITY_ID;
    },
    voteUsed() {
      return Number(this.voteState.used || 0);
    },
    voteResidue() {
      return Number(this.voteState.residue || 0);
    },
    voteOpen() {
      return Number(this.voteState.open || 0);
    },
    voteSubmitted() {
      return Number(this.voteCache.isVote || this.voteState.submitted || 0) === 1;
    },
    selectedCount() {
      return Number(this.voteCache.countWorks || 0);
    },
    selectedWorks() {
      const works = [];
      const ids = new Set();
      (this.voteCache.categoryVos || []).forEach((category) => {
        (category.worksVo || []).forEach((work) => {
          const id = Number(work && work.id);
          if (!id || ids.has(id)) {
            return;
          }
          ids.add(id);
          works.push({
            ...work,
            activityId: Number(work.activityId || this.activityId),
            categoryName: work.categoryName || category.name || "",
            isCheck: 1,
          });
        });
      });
      return works;
    },
    selectedResidue() {
      return Math.max(this.voteQuota - this.selectedCount, 0);
    },
    minimumTotalVotes() {
      if (this.useGraduationExpertVoteRule) {
        const categoryMinimumTotal = this.graduationCategoryVoteStats
          .reduce((total, item) => total + Number(item.minimum || 0), 0);
        if (categoryMinimumTotal > 0) {
          return Math.min(this.voteQuota, categoryMinimumTotal + 1);
        }
        return Math.ceil(this.voteQuota * GRADUATION_EXPERT_MINIMUM_RATIO);
      }
      return 1;
    },
    useGraduationExpertVoteRule() {
      return Number(this.activityId) === DEFAULT_ACTIVITY_ID && this.isExpertVoteMode;
    },
    graduationCategoryVoteStats() {
      if (Number(this.activityId) !== DEFAULT_ACTIVITY_ID) {
        return [];
      }
      const poolStats = (this.voteCache.plateVos || [])
        .map((item) => ({
          id: Number(item.id || 0),
          name: item.name || "未分类",
          selected: Number(item.voteSelected || 0),
          quota: Number(item.voteQuota || 0),
          quotaText: String(item.voteQuotaText || item.voteQuota || 0),
          minimum: Number(item.voteMin || 0),
          minimumText: String(item.voteMin || 0),
          worksTotal: Number(item.worksTotal || 0),
        }))
        .filter((item) => item.id > 0 && item.worksTotal > 0);
      if (poolStats.length > 0) {
        return poolStats;
      }
      const selectedCountMap = new Map();
      (this.voteCache.categoryVos || []).forEach((category) => {
        selectedCountMap.set(Number(category.id || 0), (category.worksVo || []).length);
      });
      const categoryCounts = this.categoryWorkCounts || {};
      const totalWorks = Object.values(categoryCounts).reduce((total, value) => total + Number(value || 0), 0);
      return (this.categoryTabs || [])
        .map((item) => {
          const id = Number(item.id || 0);
          const worksTotal = Number(categoryCounts[id] || 0);
          const hasCounts = totalWorks > 0;
          const quota = hasCounts ? this.calcGraduationAllocatedVotes(this.voteQuota, worksTotal, totalWorks) : 0;
          const minimum = hasCounts ? Math.ceil(quota * GRADUATION_EXPERT_MINIMUM_RATIO) : 0;
          return {
            id,
            name: item.name || "未分类",
            selected: selectedCountMap.get(id) || 0,
            quota,
            quotaText: hasCounts ? this.formatVoteNumber(quota) : "计算中",
            minimum,
            minimumText: hasCounts ? String(minimum) : "计算中",
            worksTotal: hasCounts ? worksTotal : 1,
          };
        })
        .filter((item) => item.id > 0);
    },
    showGraduationVotePanel() {
      return Number(this.activityId) === DEFAULT_ACTIVITY_ID
        && this.voteOpen === 1
        && this.graduationCategoryVoteStats.length > 0;
    },
    graduationCategoryVoteShortage() {
      if (!this.useGraduationExpertVoteRule || this.graduationCategoryVoteStats.length === 0) {
        return null;
      }
      return this.graduationCategoryVoteStats.find((item) => item.selected < item.minimum) || null;
    },
    voteSelectionValid() {
      if (this.selectedCount < this.minimumTotalVotes || this.selectedCount > this.voteQuota) {
        return false;
      }
      return !this.graduationCategoryVoteShortage;
    },
    voteMessage() {
      return this.voteState.message || "投票活动未开启";
    },
    voteModeLabel() {
      return Number(this.voteState.expertUser || 0) === 1 ? "专家投票" : "大众投票";
    },
    isExpertVoteMode() {
      return Number(this.voteState.expertUser || 0) === 1;
    },
    summaryTitle() {
      if (this.voteOpen !== 1) {
        return this.voteMessage;
      }
      if (!this.isLogin) {
        return `当前为${this.voteModeLabel}，登录后可选择作品并一次性提交`;
      }
      if (this.voteSubmitted) {
        return `当前为${this.voteModeLabel}，您已完成提交，前台不可再修改`;
      }
      return `当前为${this.voteModeLabel}，请先勾选作品，最后统一提交`;
    },
    emptyStateText() {
      return this.worksParams.keyword ? "未找到符合条件的作品" : "暂无作品";
    },
    submitButtonDisabled() {
      if (this.voteOpen !== 1) {
        return true;
      }
      if (this.submittingCache || this.voteSubmitted) {
        return true;
      }
      return this.isLogin && !this.voteSelectionValid;
    },
    submitButtonText() {
      if (this.voteOpen !== 1) {
        return "投票未开启";
      }
      if (!this.isLogin) {
        return "登录后投票";
      }
      if (this.submittingCache) {
        return "提交中...";
      }
      if (this.voteSubmitted) {
        return "已提交";
      }
      return `确认提交（${this.selectedCount}/${this.voteQuota}）`;
    },
    submitBarTitle() {
      if (!this.isLogin) {
        return "登录后可使用缓存式投票";
      }
      if (this.voteSubmitted) {
        return "本轮投票已提交完成";
      }
      return `已选择 ${this.selectedCount} 个作品`;
    },
    submitBarDesc() {
      if (!this.isLogin) {
        return "先选择作品，最后统一提交；提交后前台不可修改。";
      }
      if (this.voteSubmitted) {
        return '您已完成提交，不可再修改'
      }
      if (this.useGraduationExpertVoteRule) {
        return "请参考左侧类别票数统计完成投票，提交后前台不可修改。";
      }
      return "请按类别要求选择作品，最后统一提交。";
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler(value) {
        if (this.$route.name !== "WorkCompetition") {
          return;
        }
        const routeId = Number(value) || DEFAULT_ACTIVITY_ID;
        if (this.activityInitialized && routeId === Number(this.activityId)) {
          this.restoreListParamsFromRoute();
          this.restoreListScrollPosition();
          return;
        }
        this.activityId = routeId;
        this.activeCategoryId = 0;
        this.activityObj = {};
        this.entryReady = false;
        this.entryBlocked = false;
        this.activityRequestId += 1;
        this.worksRequestId += 1;
        this.categoryCountRequestId += 1;
        this.categoryTabs = [];
        this.categoryWorkCounts = {};
        this.worksList = [];
        this.voteState = createDefaultVoteState();
        this.voteCache = createDefaultVoteCache(routeId);
        this.selectedPanelExpanded = false;
        this.mutatingWorkIds = [];
        this.worksParams.activityId = String(routeId);
        this.restoreListParamsFromRoute();
        this.syncUserId();
        this.getActivity().then(() => {
          if (routeId === Number(this.activityId)) {
            this.activityInitialized = true;
            this.restoreListScrollPosition();
          }
        });
      },
    },
    isLogin() {
      this.syncUserId();
      this.worksParams.page = 1;
      this.getActivity();
    },
    selectedCount(value) {
      if (value <= 0) {
        this.selectedPanelExpanded = false;
      }
    },
  },
  activated() {
    if (this.$route.name !== "WorkCompetition") {
      return;
    }
    this.restoreListParamsFromRoute();
    this.restoreListScrollPosition();
  },
  methods: {
    syncUserId() {
      this.worksParams.uid = this.userInfo && this.userInfo.uid ? this.userInfo.uid : 0;
    },
    restoreListParamsFromRoute() {
      const query = this.$route.query || {};
      const page = Number(query.page);
      const filterId = this.useCategoryFilter
        ? Number(query.categoryId || query.plateId)
        : Number(query.plateId || query.categoryId);
      this.worksParams.page = Number.isInteger(page) && page > 0 ? page : 1;
      this.worksParams.plateId = !this.useCategoryFilter && Number.isInteger(filterId) && filterId > 0 ? filterId : 0;
      this.worksParams.categoryId = this.useCategoryFilter && Number.isInteger(filterId) && filterId > 0 ? filterId : 0;
      this.worksParams.keyword = String(query.keyword || "").trim();
      this.activeCategoryId = this.useCategoryFilter ? this.worksParams.categoryId : this.worksParams.plateId;

      try {
        const saved = JSON.parse(sessionStorage.getItem(LIST_SCROLL_STORAGE_KEY) || "null");
        if (saved && Number(saved.activityId) === Number(this.activityId)) {
          this.pendingRestoreScrollY = Number(saved.scrollY) || 0;
          sessionStorage.removeItem(LIST_SCROLL_STORAGE_KEY);
        }
      } catch (error) {
        sessionStorage.removeItem(LIST_SCROLL_STORAGE_KEY);
      }
    },
    syncListRouteState() {
      const query = { ...this.$route.query };
      if (this.worksParams.page > 1) {
        query.page = String(this.worksParams.page);
      } else {
        delete query.page;
      }
      delete query.plateId;
      delete query.categoryId;
      if (this.useCategoryFilter && Number(this.worksParams.categoryId) > 0) {
        query.categoryId = String(this.worksParams.categoryId);
      }
      if (!this.useCategoryFilter && Number(this.worksParams.plateId) > 0) {
        query.plateId = String(this.worksParams.plateId);
      }
      if (this.worksParams.keyword) {
        query.keyword = this.worksParams.keyword;
      } else {
        delete query.keyword;
      }
      const path = `/WorkCompetition/${Number(this.activityId) || DEFAULT_ACTIVITY_ID}`;
      return this.$router.replace({ path, query }).catch((error) => {
        if (error && error.name === "NavigationDuplicated") {
          return null;
        }
        throw error;
      });
    },
    restoreListScrollPosition() {
      if (this.pendingRestoreScrollY == null) {
        return;
      }
      const scrollY = this.pendingRestoreScrollY;
      this.pendingRestoreScrollY = null;
      this.$nextTick(() => {
        window.scrollTo({ top: scrollY, behavior: "auto" });
      });
    },
    applyActivityVoteContext(context = {}) {
      this.voteState = {
        open: Number(context.voteOpen || 0),
        submitted: Number(context.voteSubmitted || 0),
        expertUser: Number(context.expertUser || 0),
        quota: Number(context.voteQuota || 0),
        used: Number(context.voteUsed || 0),
        residue: Number(context.voteResidue || 0),
        message: context.voteMessage || "投票活动未开启",
      };
    },
    applyVoteCache(cache = {}) {
      this.voteCache = {
        ...createDefaultVoteCache(this.activityId),
        ...(cache || {}),
        activityId: Number((cache && cache.activityId) || this.activityId || DEFAULT_ACTIVITY_ID),
        categoryVos: Array.isArray(cache.categoryVos) ? cache.categoryVos : [],
        plateVos: Array.isArray(cache.plateVos) ? cache.plateVos : [],
        countWorks: Number(cache.countWorks || 0),
        countCheckWorks: Number(cache.countCheckWorks || 0),
        expertVotes: Number(cache.expertVotes || this.voteQuota || 0),
        isVote: Number(cache.isVote || 0),
      };
    },
    getSelectedIdSet() {
      const ids = new Set();
      (this.voteCache.categoryVos || []).forEach((category) => {
        (category.worksVo || []).forEach((work) => {
          ids.add(Number(work.id));
        });
      });
      return ids;
    },
    syncWorksSelection() {
      const selectedIds = this.getSelectedIdSet();
      this.worksList = this.worksList.map((item) => ({
        ...item,
        isCheck: selectedIds.has(Number(item.id)) ? 1 : 0,
      }));
    },
    loadGraduationCategoryWorkCounts(activityId = this.activityId, activityRequestId = this.activityRequestId) {
      if (Number(activityId) !== DEFAULT_ACTIVITY_ID || !this.useCategoryFilter || !this.categoryTabs.length) {
        this.categoryWorkCounts = {};
        return Promise.resolve(null);
      }
      const requestId = ++this.categoryCountRequestId;
      const requests = this.categoryTabs.map((category) => {
        const categoryId = Number(category.id || 0);
        if (!categoryId) {
          return Promise.resolve(null);
        }
        return getWorksList({
          activityId: String(activityId),
          uid: this.worksParams.uid || 0,
          limit: 1,
          page: 1,
          plateId: 0,
          categoryId,
          keyword: "",
        })
          .then((res) => ({
            categoryId,
            total: Number(res && res.total || 0),
          }))
          .catch(() => ({
            categoryId,
            total: 0,
          }));
      });
      return Promise.all(requests).then((items) => {
        if (
          requestId !== this.categoryCountRequestId
          || activityRequestId !== this.activityRequestId
          || Number(activityId) !== Number(this.activityId)
        ) {
          return null;
        }
        const counts = {};
        items.forEach((item) => {
          if (item && item.categoryId) {
            counts[item.categoryId] = item.total;
          }
        });
        this.categoryWorkCounts = counts;
        return counts;
      });
    },
    calcGraduationAllocatedVotes(totalQuota, categoryWorks, totalWorks) {
      if (!totalQuota || !categoryWorks || !totalWorks) {
        return 0;
      }
      return Number(totalQuota) * Number(categoryWorks) / Number(totalWorks);
    },
    formatVoteNumber(value) {
      const numericValue = Number(value || 0);
      if (Number.isInteger(numericValue)) {
        return String(numericValue);
      }
      return numericValue.toFixed(1).replace(/\.0$/, "");
    },
    loadVoteCache(activityId = this.activityId, activityRequestId = this.activityRequestId) {
      if (!this.isLogin || this.voteOpen !== 1) {
        if (activityRequestId !== this.activityRequestId || Number(activityId) !== Number(this.activityId)) {
          return Promise.resolve(null);
        }
        this.applyVoteCache(createDefaultVoteCache(this.activityId));
        this.syncWorksSelection();
        return Promise.resolve(null);
      }
      return getCache({ activityId })
        .then((res) => {
          if (activityRequestId !== this.activityRequestId || Number(activityId) !== Number(this.activityId)) {
            return null;
          }
          this.applyVoteCache((res && res.data) || {});
          this.syncWorksSelection();
          return res;
        })
        .catch((err) => {
          if (activityRequestId !== this.activityRequestId || Number(activityId) !== Number(this.activityId)) {
            return null;
          }
          this.applyVoteCache(createDefaultVoteCache(this.activityId));
          this.syncWorksSelection();
          this.$message.error(this.extractErrorMessage(err) || "投票缓存加载失败");
          return null;
        });
    },
    getActivity(reloadWorks = true) {
      const requestId = ++this.activityRequestId;
      const activityId = Number(this.activityId) || DEFAULT_ACTIVITY_ID;
      const uid = this.worksParams.uid || 0;
      return activityDetail(activityId, { isShow: 0, uid })
        .then(async (res) => {
          if (requestId !== this.activityRequestId || activityId !== Number(this.activityId)) {
            return null;
          }
          this.activityObj = res.data || {};
          const publicContext = this.activityObj.voteContext || {};
          let userContext = publicContext;
          if (this.isLogin) {
            try {
              const accessRes = await getExpertVoteAccess(activityId);
              if (requestId !== this.activityRequestId || activityId !== Number(this.activityId)) {
                return null;
              }
              userContext = (accessRes && accessRes.data) || publicContext;
            } catch (error) {
              if (requestId !== this.activityRequestId || activityId !== Number(this.activityId)) {
                return null;
              }
              userContext = publicContext;
            }
          }
          if (this.isExpertVoteEntryBlocked(userContext)) {
            this.blockExpertVoteEntry();
            return null;
          }
          this.entryBlocked = false;
          this.entryReady = true;
          this.applyActivityVoteContext(userContext);
          const optionGroups = normalizeVoteOptionGroups(
            this.activityObj.plateVos,
            this.activityObj.categoryVos
          );
          this.categoryTabs = this.useCategoryFilter ? optionGroups.categoryVos : optionGroups.plateVos;
          this.loadGraduationCategoryWorkCounts(activityId, requestId);
          await this.loadVoteCache(activityId, requestId);
          if (requestId !== this.activityRequestId || activityId !== Number(this.activityId)) {
            return null;
          }
          if (reloadWorks) {
            return this.worksListFunc();
          }
          return null;
        })
        .catch((err) => {
          if (requestId !== this.activityRequestId || activityId !== Number(this.activityId)) {
            return null;
          }
          this.voteState = createDefaultVoteState();
          this.voteCache = createDefaultVoteCache(this.activityId);
          this.categoryTabs = [];
          this.worksList = [];
          this.worksParams.total = 0;
          this.worksParams.totalPage = 0;
          this.$message.error((err && (err.msg || (err.data && err.data.msg))) || "活动信息加载失败");
          return null;
        });
    },
    isExpertVoteEntryBlocked(context = {}) {
      return Number(context.expertUser || 0) !== 1
        && String(context.voteMessage || "").indexOf("专家投票中") !== -1;
    },
    blockExpertVoteEntry() {
      if (this.entryBlocked) {
        return;
      }
      this.entryBlocked = true;
      this.entryReady = false;
      this.worksList = [];
      this.worksParams.total = 0;
      this.worksParams.totalPage = 0;
      this.$warning({
        title: "提示",
        content: "专家投票中",
        okText: "确定",
        onOk: () => this.$router.replace(this.getCompetitionHomePath()),
      });
    },
    getCompetitionHomePath() {
      return this.isCashmereActivity ? "/ContestInfo/cashmere-design" : "/Graduation";
    },
    worksListFunc() {
      const requestId = ++this.worksRequestId;
      const params = {
        ...this.worksParams,
        activityId: String(this.activityId),
        plateId: this.useCategoryFilter ? 0 : Number(this.worksParams.plateId) || 0,
        categoryId: this.useCategoryFilter ? Number(this.worksParams.categoryId) || 0 : 0,
      };
      this.loading = true;
      return getWorksList(params)
        .then((res) => {
          if (requestId !== this.worksRequestId) {
            return;
          }
          const selectedIds = this.getSelectedIdSet();
          this.worksList = (res.data || []).map((item) => ({
            ...item,
            ...normalizeVoteMeta(item),
            isCheck: selectedIds.has(Number(item.id)) ? 1 : 0,
          }));
          this.worksParams.total = Number(res.total || 0);
          this.worksParams.totalPage = Number(res.totalPage || 0);
        })
        .catch((err) => {
          if (requestId === this.worksRequestId) {
            this.$message.error(this.extractErrorMessage(err) || "作品列表加载失败");
          }
          return null;
        })
        .finally(() => {
          if (requestId === this.worksRequestId) {
            this.loading = false;
          }
        });
    },
    handleClickActive(id) {
      const categoryId = Number(id) || 0;
      if (categoryId === Number(this.activeCategoryId) && this.worksParams.page === 1) {
        return;
      }
      this.activeCategoryId = categoryId;
      this.worksParams.page = 1;
      this.worksParams.plateId = this.useCategoryFilter ? 0 : categoryId;
      this.worksParams.categoryId = this.useCategoryFilter ? categoryId : 0;
      this.syncListRouteState();
      this.worksListFunc();
    },
    handleSearch() {
      this.worksParams.page = 1;
      this.syncListRouteState();
      this.worksListFunc();
    },
    handleResetSearch() {
      if (!this.worksParams.keyword) {
        return;
      }
      this.worksParams.keyword = "";
      this.worksParams.page = 1;
      this.syncListRouteState();
      this.worksListFunc();
    },
    handlePageChange(page) {
      if (page === this.worksParams.page) {
        return;
      }
      this.worksParams.page = page;
      this.syncListRouteState();
      this.worksListFunc().then(() => {
        this.$nextTick(() => {
          const target = this.$refs.worksListTop;
          if (target && typeof target.scrollIntoView === "function") {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        });
      });
    },
    async handleClickToDetails(id) {
      await this.syncListRouteState();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.pendingRestoreScrollY = scrollY;
      sessionStorage.setItem(LIST_SCROLL_STORAGE_KEY, JSON.stringify({
        activityId: this.activityId,
        scrollY,
      }));
      this.$router.push({
        name: "WorkCompetitionDetail",
        params: { id },
      });
    },
    isWorkSelected(work) {
      return Number(work && work.isCheck) === 1;
    },
    handleVote(work) {
      if (this.voteOpen !== 1) {
        this.$message.error(this.voteMessage);
        return;
      }
      if (!this.isLogin) {
        toLogin(true, this.$route.fullPath);
        return;
      }
      if (this.voteSubmitted) {
        this.$message.warning("本轮投票已提交，前台不可再修改");
        return;
      }
      const isCancel = this.isWorkSelected(work);
      const workId = Number(work.id);
      if (this.isVoteMutating(workId)) {
        return;
      }
      this.mutatingWorkIds.push(workId);
      setCache({
        activityId: Number(work.activityId || this.activityId),
        worksId: work.id,
        isAdd: isCancel ? 2 : 1,
      })
        .then((res) => {
          this.applyVoteCache((res && res.data) || {});
          this.syncWorksSelection();
          this.$message.success(isCancel ? "已取消选择" : "已加入待提交列表");
        })
        .catch((err) => {
          this.$message.error(this.formatVoteErrorMessage(err, work, isCancel));
        })
        .finally(() => {
          this.mutatingWorkIds = this.mutatingWorkIds.filter((id) => id !== workId);
        });
    },
    handleCancelSelected(work) {
      if (this.voteSubmitted) {
        return;
      }
      this.handleVote({ ...work, isCheck: 1 });
    },
    isVoteMutating(workId) {
      return this.mutatingWorkIds.includes(Number(workId));
    },
    selectedWorkMeta(work) {
      const parts = [];
      if (work.worksNumber) {
        parts.push(work.worksNumber);
      }
      const workType = this.workTypeLabel(work, "");
      if (workType) {
        parts.push(workType);
      }
      return parts.join(" · ") || "已加入待提交列表";
    },
    workTypeLabel(work = {}, fallback = "-") {
      const plateName = String(work.plateName || "").trim();
      const categoryName = String(work.categoryName || "").trim();
      if (Number(work.activityId || this.activityId) === DEFAULT_ACTIVITY_ID) {
        return categoryName || plateName || fallback;
      }
      if (Number(work.activityId || this.activityId) === CASHMERE_ACTIVITY_ID) {
        if (plateName === "服装类产品") {
          return "服装产品";
        }
        if (plateName === "家居及文创艺术类产品") {
          return "家居及文创艺术类作品";
        }
      }
      return plateName || categoryName || fallback;
    },
    handleSubmitVotes() {
      if (this.voteOpen !== 1) {
        this.$message.error(this.voteMessage);
        return;
      }
      if (!this.isLogin) {
        toLogin(true, this.$route.fullPath);
        return;
      }
      if (this.voteSubmitted) {
        this.$message.warning("本轮投票已提交");
        return;
      }
      if (!this.voteSelectionValid) {
        this.$message.warning(this.voteValidationMessage());
        return;
      }
      this.submittingCache = true;
      submitCheck({ activityId: this.activityId })
        .then(() => submitCache({ activityId: this.activityId }))
        .then(() => {
          this.$message.success("投票提交成功，前台已锁定");
          return this.getActivity(true);
        })
        .finally(() => {
          this.submittingCache = false;
        })
        .catch((err) => {
          this.$message.error(this.extractErrorMessage(err) || "提交失败");
        });
    },
    extractErrorMessage(err) {
      return (err && (err.msg || (err.data && err.data.msg) || err.message)) || "";
    },
    voteValidationMessage() {
      if (this.selectedCount < this.minimumTotalVotes) {
        if (this.useGraduationExpertVoteRule) {
          return `本轮专家投票至少选择 ${this.minimumTotalVotes} 个作品`;
        }
        return "请先选择作品";
      }
      if (this.graduationCategoryVoteShortage) {
        return `${this.graduationCategoryVoteShortage.name} 至少选择 ${this.graduationCategoryVoteShortage.minimum} 个作品`;
      }
      if (this.selectedCount > this.voteQuota) {
        return `当前最多只能选择 ${this.voteQuota} 个作品`;
      }
      return "当前选择不符合提交要求";
    },
    formatVoteErrorMessage(err) {
      return this.extractErrorMessage(err) || "操作失败";
    },
    shouldShowVoteCount() {
      if (Number(this.voteState.expertUser || 0) === 1) {
        return false;
      }
      const message = String(this.voteMessage || "");
      if (this.voteOpen === 1) {
        return true;
      }
      return message.indexOf("专家") === -1 && message.indexOf("未开始") === -1;
    },
    displayVoteCount(work = {}) {
      const rankingScore = work.rankingScore;
      if (rankingScore === null || rankingScore === undefined || rankingScore === "") {
        return work.votesTotal || 0;
      }
      const numericScore = Number(rankingScore);
      if (Number.isNaN(numericScore)) {
        return rankingScore;
      }
      return Number.isInteger(numericScore) ? numericScore : numericScore.toFixed(2);
    },
    voteButtonText(work) {
      if (this.voteOpen !== 1) {
        if (this.voteMessage.indexOf("专家") !== -1) {
          return "专家投票中";
        }
        if (this.voteMessage.indexOf("完成") !== -1) {
          return "已完成";
        }
        if (this.voteMessage.indexOf("结束") !== -1) {
          return "已结束";
        }
        if (this.voteMessage.indexOf("开始") !== -1) {
          return "投票未开始";
        }
        return "投票活动未开启";
      }
      if (!this.isLogin) {
        return "登录后投票";
      }
      if (this.voteSubmitted) {
        return this.isWorkSelected(work) ? "已投票" : "已提交";
      }
      if (this.isWorkSelected(work)) {
        return "取消选择";
      }
      if (this.selectedResidue <= 0) {
        return "已达上限";
      }
      return "选择作品";
    },
    voteButtonClass(work) {
      if (this.isVoteDisabled(work)) {
        return "disabled";
      }
      if (this.voteSubmitted) {
        return this.isWorkSelected(work) ? "done" : "submitted";
      }
      return this.isWorkSelected(work) ? "cancel" : "active";
    },
    isVoteDisabled(work) {
      if (this.voteOpen !== 1) {
        return true;
      }
      if (work && this.isVoteMutating(work.id)) {
        return true;
      }
      if (!this.isLogin) {
        return false;
      }
      if (this.voteSubmitted) {
        return true;
      }
      return !this.isWorkSelected(work) && this.selectedResidue <= 0;
    },
    coverImage(work) {
      return resolveWorkThumbnail(work, nopic);
    },
    handleImageError(event, work) {
      replaceBrokenImageFromCandidates(
        event,
        resolveWorkThumbnailCandidates(work),
        nopic
      );
    },
    resolveRanking(work, index) {
      if (work.ranking) {
        return work.ranking;
      }
      return (this.worksParams.page - 1) * this.worksParams.limit + index + 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.work-competition {
  min-height: calc(100vh - 220px);
  background:
    radial-gradient(circle at top left, rgba(255, 205, 145, 0.35), transparent 28%),
    linear-gradient(180deg, #f8f4ee 0%, #fffdfa 48%, #f5f7fa 100%);
}

.hero {
  position: relative;
  min-height: 252px;
  width: 100%;
  margin: 0;
  border-radius: 0;
  overflow: hidden;
  background:
    linear-gradient(90deg, rgba(160, 145, 218, 0.96) 0%, rgba(187, 171, 227, 0.9) 28%, rgba(251, 207, 191, 0.82) 62%, rgba(255, 238, 177, 0.95) 100%);
}

.hero--cashmere {
  min-height: 0;
  background: #f4f0ec;
}

.hero-cashmere-image {
  display: block;
  width: 100%;
  height: auto;
}

.hero-gradient,
.hero-glow,
.hero-ribbon {
  position: absolute;
  inset: 0;
}

.hero-gradient {
  background:
    linear-gradient(120deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0) 36%),
    linear-gradient(90deg, rgba(160, 145, 218, 0.96) 0%, rgba(187, 171, 227, 0.9) 28%, rgba(251, 207, 191, 0.82) 62%, rgba(255, 238, 177, 0.95) 100%);
}

.hero-glow {
  border-radius: 999px;
  pointer-events: none;
}

.hero-glow--left {
  top: -58px;
  left: -72px;
  right: auto;
  bottom: auto;
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0) 72%);
}

.hero-glow--right {
  top: auto;
  left: auto;
  right: -84px;
  bottom: -96px;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(255, 244, 205, 0.5) 0%, rgba(255, 244, 205, 0) 70%);
}

.hero-ribbon {
  inset: auto;
  width: 360px;
  height: 110px;
  border-radius: 999px;
  opacity: 0.28;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0));
  transform: rotate(-12deg);
  pointer-events: none;
}

.hero-ribbon--top {
  top: -12px;
  right: -56px;
}

.hero-ribbon--bottom {
  left: -64px;
  bottom: -18px;
  transform: rotate(9deg);
}

.hero-copy {
  position: relative;
  z-index: 1;
  display: flex;
  min-height: 252px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: min(900px, calc(100% - 56px));
  margin: 0 auto;
  padding: 38px 0;
  color: #fff;
  text-align: center;
}

.hero-kicker {
  margin: 0 0 14px;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-shadow: 0 6px 16px rgba(112, 84, 92, 0.16);
}

.hero-copy h1 {
  max-width: min(1400px, calc(100vw - 64px));
  margin: 0;
  color: #fff;
  font-size: 38px;
  line-height: 1.2;
  font-weight: 700;
  text-shadow: 0 10px 24px rgba(124, 92, 90, 0.16);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.competition-content {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
  padding: 36px 0 60px;
}

.summary {
  margin-bottom: 24px;
  text-align: center;
  color: #6b6257;
}

.summary-title {
  margin: 0;
  color: #46372d;
  font-size: 18px;
  font-weight: 600;
}

.summary-meta,
.plate-pools {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 14px 0 0;
}

.summary-meta span,
.plate-pool-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #7b6c60;
  font-size: 14px;
  box-shadow: 0 10px 20px rgba(123, 93, 73, 0.06);
  transition: background 0.2s, color 0.2s;
}

.plate-pool-item.plate-full {
  background: #fef0f0;
  color: #f56c6c;
}

.plate-pool-item.plate-cap {
  background: rgba(255, 255, 255, 0.82);
  color: #7b6c60;
}

.plate-pool-name {
  font-weight: 500;
}

.plate-pool-count {
  font-weight: 700;
  font-size: 15px;
}

.plate-pool-sep {
  opacity: 0.5;
}

.plate-pool-max,
.plate-pool-range {
  font-weight: 600;
}

.plate-pool-full-mark {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  background: #f56c6c;
  color: #fff;
}

.category-vote-panel {
  position: fixed;
  top: 300px;
  left: 0;
  z-index: 12;
  width: 286px;
  padding: 14px;
  border: 1px solid rgba(217, 126, 79, 0.18);
  border-left: none;
  border-radius: 0 18px 18px 0;
  background: rgba(255, 252, 247, 0.94);
  box-shadow: 0 18px 38px rgba(123, 93, 73, 0.16);
  backdrop-filter: blur(12px);
}

.category-vote-panel__title {
  margin-bottom: 10px;
  color: #3f3028;
  font-size: 16px;
  font-weight: 700;
}

.category-vote-panel__body {
  display: grid;
  gap: 8px;
  max-height: none;
  overflow: visible;
}

.category-vote-row {
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.78);
  color: #6f6054;
}

.category-vote-row.is-short {
  background: #fff4ed;
}

.category-vote-row__name {
  color: #3f3028;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  word-break: break-all;
}

.category-vote-row__stats {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 12px;
  line-height: 1.45;
}

.category-vote-row__stats span:first-child {
  color: #d97e4f;
  font-weight: 700;
}

.category-vote-panel__footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(217, 126, 79, 0.12);
  color: #8a7664;
  font-size: 12px;
  line-height: 1.5;
}

.tabs-box {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 28px;
}

.search-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 28px;
}

.search-input {
  width: min(420px, 100%);
}

.search-box ::v-deep(.ant-input-search) {
  display: block;
}

.search-box ::v-deep(.ant-input-search .ant-input) {
  height: 48px;
  padding: 0 18px;
  border: 1px solid rgba(217, 126, 79, 0.22);
  border-right: none;
  border-radius: 999px 0 0 999px;
  background: rgba(255, 255, 255, 0.9);
  color: #4a3b31;
  font-size: 15px;
  box-shadow: 0 14px 28px rgba(123, 93, 73, 0.08);
}

.search-box ::v-deep(.ant-input-search .ant-input::placeholder) {
  color: #b5a08d;
}

.search-box ::v-deep(.ant-input-search .ant-input:hover),
.search-box ::v-deep(.ant-input-search .ant-input:focus) {
  border-color: rgba(217, 126, 79, 0.42);
  box-shadow: 0 16px 30px rgba(123, 93, 73, 0.12);
}

.search-box ::v-deep(.ant-input-search-button) {
  height: 48px;
  padding: 0 24px;
  border: 1px solid #d97e4f;
  border-radius: 0 999px 999px 0;
  background: linear-gradient(135deg, #ffb06d, #ea7c44);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 14px 28px rgba(123, 93, 73, 0.1);
}

.search-box ::v-deep(.ant-input-search-button:hover),
.search-box ::v-deep(.ant-input-search-button:focus) {
  border-color: #d97e4f;
  background: linear-gradient(135deg, #ffb06d, #ea7c44);
  color: #fff;
  opacity: 0.94;
}

.search-box ::v-deep(.ant-input-search .ant-input-group-addon) {
  overflow: hidden;
  border-radius: 0 999px 999px 0;
}

.search-reset {
  min-width: 92px;
  height: 48px;
  padding: 0 22px;
  border: 1px solid rgba(217, 126, 79, 0.3);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: #b36a42;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(123, 93, 73, 0.08);
  transition: all 0.2s ease, box-shadow 0.2s ease;
}

.search-reset:hover {
  border-color: #d97e4f;
  background: rgba(255, 244, 238, 0.96);
  color: #d97e4f;
  box-shadow: 0 16px 30px rgba(123, 93, 73, 0.12);
}

.tab-item {
  padding: 11px 18px;
  border: 1px solid #d8c8b5;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  color: #7c6857;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-item.active,
.tab-item:hover {
  border-color: #d97e4f;
  background: #d97e4f;
  color: #fff;
}

.state-block {
  padding: 52px 0;
  color: #8c8074;
  text-align: center;
  font-size: 16px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.card {
  overflow: hidden;
  border: 1px solid rgba(217, 126, 79, 0.18);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 36px rgba(123, 93, 73, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 42px rgba(123, 93, 73, 0.14);
}

.cover-wrap {
  position: relative;
  padding: 14px 14px 0;
}

.cover {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 16px;
  object-fit: cover;
  background: #f3eee8;
}

.rank-tag {
  position: absolute;
  padding: 6px 10px;
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
}

.rank-tag {
  top: 24px;
  left: 24px;
  background: rgba(29, 25, 31, 0.68);
}

.card-body {
  padding: 16px 18px 18px;
}

.title {
  margin: 0 0 12px;
  color: #2f2620;
  font-size: 18px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.meta-line {
  margin: 0 0 8px;
  color: #7b6c60;
  font-size: 14px;
  line-height: 1.5;
}

.footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee4d8;
}

.footer-row--compact {
  justify-content: flex-end;
}

.votes {
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: #d97e4f;
}

.votes strong {
  font-size: 28px;
  line-height: 1;
}

.vote-btn,
.submit-bar__btn {
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.vote-btn {
  min-width: 112px;
  padding: 10px 16px;
  font-size: 14px;
}

.vote-btn.active,
.submit-bar__btn {
  background: linear-gradient(135deg, #ffb06d, #ea7c44);
  color: #fff;
}

.vote-btn.cancel {
  background: #f4e6de;
  color: #a15e3c;
}

.vote-btn.done {
  background: #e7f6ee;
  color: #1f8b55;
}

.vote-btn.submitted {
  background: #f0ede8;
  color: #8f857c;
}

.vote-btn.disabled,
.submit-bar__btn.disabled {
  background: #ece6df;
  color: #a39a90;
  cursor: not-allowed;
}

.vote-btn:not(.disabled):hover,
.submit-bar__btn:not(.disabled):hover {
  transform: translateY(-1px);
}

.tabs-page {
  display: flex;
  justify-content: center;
  margin-top: 34px;
}

.tabs-page ::v-deep(.ant-pagination-item) {
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 10px 20px rgba(123, 93, 73, 0.06);
}

.tabs-page ::v-deep(.ant-pagination-item a) {
  color: #7b6c60;
}

.tabs-page ::v-deep(.ant-pagination-item-active) {
  background: linear-gradient(135deg, #ffb06d, #ea7c44);
}

.tabs-page ::v-deep(.ant-pagination-item-active a) {
  color: #fff;
}

.tabs-page ::v-deep(.ant-pagination-prev .ant-pagination-item-link),
.tabs-page ::v-deep(.ant-pagination-next .ant-pagination-item-link) {
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #7b6c60;
}

.submit-dock {
  position: sticky;
  bottom: 16px;
  z-index: 20;
  margin-top: 28px;
  border: 1px solid rgba(217, 126, 79, 0.18);
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 18px 36px rgba(123, 93, 73, 0.16);
  backdrop-filter: blur(12px);
}

.selected-panel {
  padding: 18px 20px;
  border-bottom: 1px solid #eee5dd;
}

.selected-panel__header,
.selected-panel__header > div {
  display: flex;
  align-items: center;
}

.selected-panel__header {
  justify-content: space-between;
  margin-bottom: 14px;
}

.selected-panel__header strong {
  color: #2f2620;
  font-size: 16px;
}

.selected-panel__header span {
  margin-left: 10px;
  color: #8d7d71;
  font-size: 13px;
}

.selected-panel__close,
.selected-item__remove,
.submit-bar__toggle {
  border: 0;
  background: transparent;
  cursor: pointer;
}

.selected-panel__close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #876f60;
}

.selected-panel__close:hover {
  background: #f7eee8;
}

.selected-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  max-height: 228px;
  overflow-y: auto;
  padding-right: 4px;
}

.selected-item {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 30px;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 8px;
  border: 1px solid #eee5dd;
  border-radius: 8px;
  background: #fffdfa;
}

.selected-item__cover {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  object-fit: cover;
  background: #f2eee9;
}

.selected-item__content {
  min-width: 0;
}

.selected-item__content strong,
.selected-item__content span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-item__content strong {
  color: #382d26;
  font-size: 14px;
  line-height: 1.5;
}

.selected-item__content span {
  margin-top: 4px;
  color: #938378;
  font-size: 12px;
}

.selected-item__remove {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: #a46a4a;
}

.selected-item__remove:hover {
  background: #f8e9e1;
}

.selected-item__remove:disabled {
  color: #bbb1aa;
  cursor: wait;
}

.selected-panel__empty {
  padding: 20px 0;
  color: #9a8c82;
  text-align: center;
}

.selected-panel-enter-active,
.selected-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.selected-panel-enter,
.selected-panel-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.submit-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 18px;
  padding: 18px 22px;
}

.submit-bar__toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 126px;
  padding: 9px 12px;
  border-radius: 8px;
  background: #f7eee8;
  color: #9b603f;
  font-size: 14px;
}

.submit-bar__toggle .anticon {
  transition: transform 0.2s ease;
}

.submit-bar__toggle.expanded .anticon {
  transform: rotate(180deg);
}

.submit-bar__info {
  min-width: 0;
  flex: 1;
}

.submit-bar__title {
  color: #2f2620;
  font-size: 18px;
  font-weight: 600;
}

.submit-bar__desc {
  margin-top: 6px;
  color: #7b6c60;
  font-size: 14px;
}

.submit-bar__btn {
  min-width: 180px;
  padding: 12px 22px;
  font-size: 15px;
  font-weight: 600;
}

@media (max-width: 1200px) {
  .category-vote-panel {
    position: static;
    width: auto;
    margin: -4px 0 24px;
  }

  .category-vote-panel__body {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    max-height: none;
    overflow: visible;
  }

  .grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .hero {
    min-height: 220px;
  }

  .hero--cashmere {
    min-height: 0;
  }

  .hero-copy h1 {
    font-size: 30px;
  }

  .summary {
    text-align: left;
  }

  .summary-meta,
  .plate-pools {
    justify-content: flex-start;
  }

  .category-vote-panel__body {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .selected-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .hero {
    min-height: 188px;
    width: 100%;
    margin-top: 0;
    border-radius: 0;
  }

  .hero--cashmere {
    min-height: 0;
  }

  .hero-copy {
    min-height: 188px;
    width: calc(100% - 32px);
    padding: 24px 0;
  }

  .hero-copy h1 {
    font-size: 22px;
    line-height: 1.45;
  }

  .hero-kicker {
    margin-bottom: 10px;
    font-size: 16px;
  }

  .grid {
    grid-template-columns: 1fr;
  }

  .category-vote-panel {
    padding: 12px;
    border-radius: 14px;
  }

  .category-vote-panel__body {
    grid-template-columns: 1fr;
  }

  .search-box {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input,
  .search-reset {
    width: 100%;
  }

  .footer-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .vote-btn {
    width: 100%;
  }

  .submit-dock {
    bottom: 8px;
    border-radius: 12px;
  }

  .selected-panel {
    padding: 14px;
  }

  .selected-list {
    grid-template-columns: 1fr;
    max-height: 252px;
  }

  .submit-bar {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px 12px;
    padding: 12px 14px;
  }

  .submit-bar__toggle {
    min-width: 0;
    justify-content: center;
  }

  .submit-bar__info {
    grid-column: 1 / -1;
    grid-row: 1;
  }

  .submit-bar__toggle {
    grid-column: 1;
    grid-row: 2;
  }

  .submit-bar__btn {
    grid-column: 2;
    grid-row: 2;
    min-width: 138px;
  }

  .submit-bar__title {
    font-size: 16px;
  }

  .submit-bar__desc {
    margin-top: 3px;
    font-size: 12px;
  }
}
</style>
